/**
 * Casamento de nomes de produto (Excel / lotes) com o cadastro de Pontuação.
 */

/** Tipos que aparecem no Excel mas não no cadastro de pontuação (só estes são removidos). */
const PREFIXOS_EXCEL_APENAS = ['estante'];

/** Cores conhecidas no fim do nome do produto (mais longas primeiro). */
const CORES_REFERENCIA = [
  'OFF WHITE/CEDRO/RIPADO 3D',
  'CEDRO NATURAL/OFF WHITE',
  'OFF WHITE/CEDRO/RIPADO',
  'OFF WHITE/NATURALE NEW',
  'OFF WHITE/CEDRO',
  'PRETO/NATURALE NEW',
  'FENDI/FREIJO NOBRE',
  'FREIJO NOBRE/FENDI',
  'OFF/CEDRO/RIPADO',
  'CEDRO/RIPADO 3D',
  'CEDRO/OFF WHITE',
  'BRANCO/CEDRO',
  'FREIJO/FENDI',
  'FENDI/FREIJO',
  'PRETO/NATURALE',
  'PRETO/CEDRO',
  'CEDRO/PRETO',
  'OFF/NATURALE',
  'OFF/CEDRO',
  'CEDRO/OFF',
  'NATURALE NEW',
  'OFF WHITE',
  'RIPADO 3D',
  'NATURALE',
  'FREIJO NOBRE',
  'FREIJO',
  'BRANCO',
  'BRANCA',
  'FENDI',
  'CEDRO',
  'PRETO',
  'OUTROS',
].sort((a, b) => b.length - a.length);

/** Nomes do Excel / legado que diferem do cadastro de pontuação. */
const SUBSTITUICOES_NOME_EXCEL: Array<{ de: RegExp; para: string }> = [
  { de: /\bRACK\s+[\d.,]+\s*X\s*180\s+LEGACY\b/gi, para: 'RACK LEGACY 1.8' },
  { de: /\bPAINEL\s+AVANTI\s+1\.35\b/gi, para: 'PAINEL AVANTI 1.36' },
  { de: /\bPAINEL\s+AVANTI\s+1\.80\b/gi, para: 'PAINEL AVANTI 1.8' },
  // Nome antigo errado — o cadastro real é BUFFET ARON 2.0
  { de: /\bBUFFET\s+ARON\s+4\s+PORTAS\b/gi, para: 'BUFFET ARON 2.0' },
];

export function normalizarChaveProduto(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
}

function normalizarEspacos(s: string): string {
  return String(s || '').replace(/\s+/g, ' ').trim();
}

function textoTerminaComToken(texto: string, token: string): boolean {
  const t = normalizarChaveProduto(texto);
  const tok = normalizarChaveProduto(token);
  if (!tok) return false;
  return t === tok || t.endsWith(` ${tok}`);
}

function removerTokenDoFinal(texto: string, token: string): string {
  const esc = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(?:^|\\s)${esc}$`, 'i');
  const m = texto.match(re);
  if (!m || m.index == null) return texto;
  return texto.slice(0, m.index + (m[0].startsWith(' ') ? 1 : 0)).trim();
}

function textoComecaComToken(texto: string, token: string): boolean {
  const t = normalizarChaveProduto(texto);
  const tok = normalizarChaveProduto(token);
  if (!tok) return false;
  return t === tok || t.startsWith(`${tok} `) || t.startsWith(`${tok}/`);
}

function removerTokenDoInicio(texto: string, token: string): string {
  const esc = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`^${esc}(?:\\s|/)+`, 'i');
  const next = texto.replace(re, '').trim();
  if (next !== texto) return next;
  if (normalizarChaveProduto(texto) === normalizarChaveProduto(token)) return '';
  return texto;
}

function juntarPartesCor(partes: string[]): string {
  return partes
    .join(' ')
    .replace(/\bOFF OFF\b/gi, 'OFF')
    .replace(/\s+/g, ' ')
    .trim();
}

export function extrairCorDoFinal(texto: string): { corProduto?: string; resto: string } {
  let resto = normalizarEspacos(texto);
  const partes: string[] = [];
  let guard = 0;

  while (guard++ < 10 && resto) {
    let achou = false;
    for (const cor of CORES_REFERENCIA) {
      if (textoTerminaComToken(resto, cor)) {
        partes.unshift(cor);
        resto = removerTokenDoFinal(resto, cor);
        achou = true;
        break;
      }
    }
    if (!achou) break;
  }

  if (partes.length === 0) return { resto };
  return { corProduto: juntarPartesCor(partes), resto };
}

/** Tira CEDRO, PRETO, OFF WHITE, FENDI, NATURALE, FREIJO etc. do começo do texto. */
export function removerCoresDaFrente(texto: string): string {
  let resto = normalizarEspacos(texto);
  const original = resto;
  let guard = 0;
  while (guard++ < 10 && resto) {
    let achou = false;
    for (const cor of CORES_REFERENCIA) {
      if (textoComecaComToken(resto, cor)) {
        const next = removerTokenDoInicio(resto, cor);
        if (next !== resto) {
          resto = next;
          achou = true;
          break;
        }
      }
    }
    if (!achou) break;
  }
  return resto || original;
}

/** Descrição da peça sem cor no início nem no fim (ex.: CEDRO LATERAL DIR → LATERAL DIR). */
export function descricaoPecaSemCor(texto: string): string {
  const original = normalizarEspacos(texto);
  const semFrente = removerCoresDaFrente(original);
  const semFim = extrairCorDoFinal(semFrente).resto;
  return semFim || semFrente || original;
}

/** Nome do produto sem cor na frente nem no fim. */
export function nomeProdutoSemCor(texto: string): string {
  const original = normalizarEspacos(texto);
  const semFim = extrairCorDoFinal(original).resto;
  const semFrente = removerCoresDaFrente(semFim);
  return semFrente || semFim || original;
}

function removerPrefixosExcelApenas(nomeBruto: string): string {
  let limpo = nomeBruto.trim();
  for (const pref of PREFIXOS_EXCEL_APENAS) {
    limpo = limpo.replace(new RegExp(`^${pref}\\s+`, 'i'), '');
  }
  return limpo.trim();
}

function tokensCatalogoEmOrdem(textoNorm: string, nomeCatalogo: string): boolean {
  const tokens = normalizarTextoProdutoImportacao(nomeCatalogo)
    .split(/\s+/)
    .filter((t) => t.length >= 2);
  if (tokens.length < 2) return false;
  let pos = 0;
  for (const tok of tokens) {
    const idx = textoNorm.indexOf(tok, pos);
    if (idx < 0) return false;
    pos = idx + tok.length;
  }
  return true;
}

export function normalizarTextoProdutoImportacao(texto: string): string {
  let s = normalizarChaveProduto(texto)
    .replace(/\bBERLIM\b/g, 'BERLIN')
    .replace(/\bPAINEL\s+AVANT\b/g, 'PAINEL AVANTI')
    .replace(/\bMESA\s+CENTRO\b/g, 'MESA DE CENTRO')
    .replace(/\bMESA\s+(?:DE\s+)?CABECEIRA\b/g, 'MESA CAB.')
    .replace(/\bGLOSS\b/g, 'GLOOS')
    .replace(/\bCDO\b/g, 'CEDRO')
    .replace(/\bSUSP\.?\b/g, ' ')
    .replace(/\bSUSPENSO\b/g, ' ')
    .replace(/\bSUSPENSA\b/g, ' ')
    // 1,8 / 1,.8 / 1 , 8 → 1.8 (comum no Excel)
    .replace(/(\d+)\s*,\s*(\.?\s*\d+)/g, (_m, intPart: string, decPart: string) => {
      const dec = String(decPart).replace(/\s+/g, '').replace(/^\./, '');
      return dec ? `${intPart}.${dec}` : intPart;
    })
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\s+/g, ' ')
    .trim();
  for (const { de, para } of SUBSTITUICOES_NOME_EXCEL) {
    s = s.replace(de, para);
  }
  // 1.80 → 1.8, 2.00 → 2.0 (mantém 1.35, 1.36…)
  s = s.replace(/\b(\d+)\.(\d*?)0+\b/g, (_m, intPart: string, decPart: string) => {
    const trimmed = decPart.replace(/0+$/, '');
    return trimmed ? `${intPart}.${trimmed}` : intPart;
  });
  return s.replace(/\s+/g, ' ').trim();
}

function parseMedidaNumero(val: string): number | null {
  const n = parseFloat(String(val || '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function medidasEquivalentes(a: string, b: string): boolean {
  const fa = parseMedidaNumero(a);
  const fb = parseMedidaNumero(b);
  if (fa == null || fb == null) return a === b;
  return Math.abs(fa - fb) < 0.02;
}

function medidaPresenteNaFonte(medidaCatalogo: string, medidasFonte: string[]): boolean {
  return medidasFonte.some((m) => medidasEquivalentes(m, medidaCatalogo));
}

function extrairMedidasTexto(textoNorm: string): string[] {
  const matches = textoNorm.match(/\b\d{1,2}\.\d{1,3}\b/g) || [];
  return [...new Set(matches)];
}

function medidaNoNomeCatalogo(nomeCatalogo: string): string | null {
  const m = normalizarTextoProdutoImportacao(nomeCatalogo).match(/\b(\d{1,2}\.\d{1,3})\b/);
  return m ? m[1] : null;
}

/** Sufixo após o nome base parece medida (1.8, 0.92, 180…). */
function restoPareceMedida(resto: string): boolean {
  const t = String(resto || '').trim();
  if (!t) return false;
  if (/\b\d{1,2}\.\d{1,3}\b/.test(t)) return true;
  if (/^\d{1,2}$/.test(t)) return false;
  if (/^\d+[,.]?\d*$/.test(t.replace(/\s+/g, ''))) return true;
  return false;
}

/**
 * Após o nome do catálogo sobrou algo que NÃO é só medida/cor —
 * ex.: "LED" em "PAINEL ESTONE 1.4 LED" → produto distinto do cadastro sem LED.
 * Mesma ideia da regra de medida: Excel mais específico ≠ cadastro mais curto.
 */
function restoEhVarianteDistinta(resto: string): boolean {
  let t = normalizarEspacos(resto);
  if (!t) return false;
  // Remove medidas tipográficas do sufixo
  t = t
    .replace(/\b\d{1,2}[.,]\d{1,3}\b/g, ' ')
    .replace(/\b\d{3,4}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!t) return false;
  // Remove cores conhecidas do final (se ainda restarem)
  const { resto: semCor } = extrairCorDoFinal(`X ${t}`);
  const sobra = normalizarEspacos(semCor.replace(/^X\s*/i, ''));
  return sobra.length > 0;
}

function existeNomeExatoNoCatalogo(fonte: string, nomes: readonly string[]): boolean {
  return nomes.some((n) => normalizarTextoProdutoImportacao(n) === fonte);
}

function variantesNomeComBarra(nomeCatalogoNorm: string): string[] {
  const nk = String(nomeCatalogoNorm || '').trim();
  if (!nk.includes('/')) return [nk];
  const segs = nk.split('/').map((s) => s.trim()).filter(Boolean);
  if (segs.length < 2) return [nk];
  const primeiro = segs[0];
  const bits = primeiro.split(/\s+/);
  const prefixo = bits.slice(0, -1).join(' ');
  const out = [primeiro];
  for (const s of segs.slice(1)) {
    out.push(prefixo ? `${prefixo} ${s}` : s);
  }
  return [...new Set(out.filter(Boolean))];
}

function pontuarMatchCatalogo(
  fonte: string,
  nomeCatalogo: string,
  nomesCatalogo: readonly string[] = []
): number {
  const nk = normalizarTextoProdutoImportacao(nomeCatalogo);
  if (nk.length < 3) return 0;

  // Nome idêntico ao Excel (ex.: BUFFET ASTURIAS ≠ BUFFET ASTURIAS 0.92)
  if (nk === fonte) {
    return 200_000 + nk.length;
  }

  for (const v of variantesNomeComBarra(nk)) {
    if (v.length < 3) continue;
    if (fonte === v) return 180_000 + v.length;
    if (fonte.startsWith(`${v} `)) {
      const resto = fonte.slice(v.length + 1).trim();
      if (!restoEhVarianteDistinta(resto)) return 160_000 + v.length;
    }
  }

  const medidasFonte = extrairMedidasTexto(fonte);
  const medidaCatalogo = medidaNoNomeCatalogo(nomeCatalogo);

  // Excel com medida extra não casa com cadastro sem medida (PAINEL ASTURIAS 1.8 ≠ PAINEL ASTURIAS)
  // Excel com variante extra (LED, etc.) não casa com cadastro sem essa variante
  if (fonte.startsWith(nk + ' ')) {
    const resto = fonte.slice(nk.length + 1).trim();
    if (restoPareceMedida(resto) || medidasFonte.length > 0) {
      if (!medidaCatalogo) return 0;
    }
    if (restoEhVarianteDistinta(resto)) return 0;
  }

  let score = 0;
  if (fonte.includes(nk)) {
    if (fonte.startsWith(nk + ' ')) {
      const resto = fonte.slice(nk.length + 1).trim();
      if ((restoPareceMedida(resto) || medidasFonte.length > 0) && !medidaCatalogo) return 0;
      if (restoEhVarianteDistinta(resto)) return 0;
    }
    score = nk.length * 10;
  } else if (fonte.startsWith(nk + ' ')) {
    const resto = fonte.slice(nk.length + 1).trim();
    if (restoEhVarianteDistinta(resto)) return 0;
    score = nk.length * 10;
  } else if (nk.startsWith(fonte + ' ')) {
    // Excel sem medida → variante com medida só se não existir o nome base no cadastro
    if (existeNomeExatoNoCatalogo(fonte, nomesCatalogo)) return 0;
    // Cadastro mais longo com variante (LED) não deve ser escolhido se o Excel não tem
    const sufixoCat = nk.slice(fonte.length + 1).trim();
    if (restoEhVarianteDistinta(sufixoCat)) return 0;
    const medidaCat = medidaNoNomeCatalogo(nomeCatalogo);
    const bonusMedida = medidaCat ? Math.max(0, 50 - (parseMedidaNumero(medidaCat) || 0) * 10) : 0;
    score = fonte.length * 9 + bonusMedida;
  } else if (tokensCatalogoEmOrdem(fonte, nomeCatalogo)) {
    score = nk.length;
  } else {
    return 0;
  }

  if (medidaCatalogo && medidaPresenteNaFonte(medidaCatalogo, medidasFonte)) {
    score += 1000;
  } else if (medidaCatalogo && medidasFonte.length > 0) {
    score -= 5000;
  } else if (!medidaCatalogo && medidasFonte.length > 0) {
    score -= 5000;
  }

  return score;
}

/** Resolve nome bruto (Excel ou lote) para o nome cadastrado na Pontuação. */
export function resolverNomeProdutoPontuacao(
  nomeBruto: string,
  nomes: readonly string[],
  textoCompleto?: string
): string {
  const fontesRaw = [nomeBruto, textoCompleto].filter((f) => normalizarChaveProduto(f)) as string[];
  const fontes = fontesRaw.map(normalizarTextoProdutoImportacao);

  for (const fonte of fontes) {
    const exato = nomes.find((n) => normalizarTextoProdutoImportacao(n) === fonte);
    if (exato) return exato;

    let melhorScore = 0;
    let melhorNome = '';
    for (const n of nomes) {
      const score = pontuarMatchCatalogo(fonte, n, nomes);
      if (score > melhorScore) {
        melhorScore = score;
        melhorNome = n;
      }
    }
    if (melhorNome) return melhorNome;
  }

  const base = removerPrefixosExcelApenas(nomeBruto) || removerPrefixosExcelApenas(textoCompleto || '');
  const { resto } = extrairCorDoFinal(base);
  return resto.trim() || base.trim();
}

export function parseProdutoCelulaExcel(
  raw: string,
  nomesPontuacao: readonly string[]
): {
  produtoNome: string;
  corProduto?: string;
  codigoProduto?: string;
  textoProdutoOriginal: string;
  encontradoCatalogo: boolean;
} {
  const textoProdutoOriginal = raw.trim();
  let codigoProduto: string | undefined;
  let s = textoProdutoOriginal.replace(/\bCDO\b/gi, 'CEDRO');
  for (let i = 0; i < 4; i++) {
    const codLongo = s.match(/\s(\d{4,})\s*$/);
    if (!codLongo) break;
    if (!codigoProduto) codigoProduto = codLongo[1];
    s = s.slice(0, -codLongo[0].length).trim();
  }

  const { corProduto, resto: semCor } = extrairCorDoFinal(s);
  const produtoNome = resolverNomeProdutoPontuacao(semCor, nomesPontuacao, s);
  const encontradoCatalogo = nomesPontuacao.some((n) => {
    const nn = normalizarTextoProdutoImportacao(n);
    const pn = normalizarTextoProdutoImportacao(produtoNome);
    if (nn === pn) return true;
    return pontuarMatchCatalogo(pn, n, nomesPontuacao) > 0 || pontuarMatchCatalogo(
      normalizarTextoProdutoImportacao(semCor),
      n,
      nomesPontuacao
    ) > 0;
  });

  return { produtoNome, corProduto, codigoProduto, textoProdutoOriginal, encontradoCatalogo };
}
